import React from 'react';
import { Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { wellnessFacilities, wellnessServices } from '../mock';

export const Wellness = () => {
  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section
        className="relative h-96 flex items-center justify-center"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=1920&q=80)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0" style={{ backgroundColor: 'rgba(16, 40, 84, 0.7)' }} />
        <div className="relative z-10 text-center px-4">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4">
            Health Club & Wellness
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Rejuvenate your body, refresh your mind, and restore your spirit
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#102854' }}>
            Your Wellness Sanctuary
          </h2>
          <p className="text-lg text-gray-600 leading-relaxed mb-4">
            At Pauline Hotels, we believe that true luxury includes caring for your wellbeing. Our comprehensive 
            health club and wellness facilities provide the perfect escape from daily stresses.
          </p>
          <p className="text-lg text-gray-600 leading-relaxed">
            Whether you seek an invigorating workout, soothing heat therapy, or restorative spa treatments, 
            our world-class facilities and expert therapists are here to elevate your wellness journey.
          </p>
        </div>
      </section>

      {/* Facilities */}
      <section className="py-16 px-4" style={{ backgroundColor: '#f9fafb' }}>
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#102854' }}>
              Our Facilities
            </h2>
            <p className="text-lg text-gray-600">
              State-of-the-art equipment and luxurious spaces designed for your comfort
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {wellnessFacilities.map((facility) => (
              <div key={facility.id} className="bg-white rounded-xl shadow-lg overflow-hidden group hover:shadow-2xl transition-all duration-300">
                <div className="h-64 overflow-hidden">
                  <img
                    src={facility.image}
                    alt={facility.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-2xl font-bold mb-3" style={{ color: '#102854' }}>
                    {facility.name}
                  </h3>
                  <p className="text-gray-600 mb-4">
                    {facility.description}
                  </p>
                  <ul className="space-y-2">
                    {facility.features.map((feature, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: '#C9A961' }} />
                        <span className="text-sm text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Wellness Services */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: '#102854' }}>
              Spa & Wellness Services
            </h2>
            <p className="text-lg text-gray-600">
              Indulge in our premium treatments designed to relax, restore, and rejuvenate
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {wellnessServices.map((service, index) => (
              <div key={index} className="bg-white border-2 rounded-xl p-6 hover:shadow-lg transition-all duration-300" style={{ borderColor: '#f3f4f6' }}>
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-xl font-bold" style={{ color: '#102854' }}>
                    {service.name}
                  </h3>
                  <div className="text-xl font-bold" style={{ color: '#C9A961' }}>
                    {service.price}
                  </div>
                </div>
                <p className="text-gray-600 text-sm">
                  {service.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operating Hours */}
      <section className="py-16 px-4" style={{ backgroundColor: '#f9fafb' }}>
        <div className="container mx-auto max-w-4xl">
          <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-2xl font-bold mb-6 text-center" style={{ color: '#102854' }}>
              Operating Hours
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#102854' }}>Gym</h3>
                <p className="text-gray-600">10:00 AM - 10:00 PM</p>
                <p className="text-sm text-gray-500 mt-1">Daily</p>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#102854' }}>Sauna & Steam Room</h3>
                <p className="text-gray-600">1:00 PM - 10:00 PM</p>
                <p className="text-sm text-gray-500 mt-1">Daily</p>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#102854' }}>Spa Services</h3>
                <p className="text-gray-600">11:00 AM - 10:00 PM</p>
                <p className="text-sm text-gray-500 mt-1">Daily by appointment</p>
              </div>
              <div className="text-center p-4 rounded-lg" style={{ backgroundColor: '#f9fafb' }}>
                <h3 className="font-semibold mb-2" style={{ color: '#102854' }}>Contact</h3>
                <div className="flex items-center justify-center space-x-2">
                  <Phone size={16} style={{ color: '#C9A961' }} />
                  <p className="text-gray-600">0779203214</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};